import { useEffect, useRef } from "react";
import "./Background.css";

interface Ball {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  gradient: CanvasGradient | null;
}

const Background = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const ballsRef = useRef<Ball[]>([]);
  const isDarkRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Check if dark mode
    const checkDarkMode = () => {
      isDarkRef.current = document.documentElement.classList.contains("dark") || 
                         document.documentElement.getAttribute("data-theme") === "dark";
    };
    checkDarkMode();

    // Colors for light/dark mode
    const getColors = () => {
      if (isDarkRef.current) {
        return [
          { start: "#00d4aa", end: "#00a080" },
          { start: "#38bdf8", end: "#0284c7" },
          { start: "#a855f7", end: "#7c3aed" },
          { start: "#00d4aa", end: "#5ff5d8" },
          { start: "#38bdf8", end: "#60a5fa" },
          { start: "#a855f7", end: "#c084fc" },
          { start: "#00d4aa", end: "#0ea5e9" },
          { start: "#38bdf8", end: "#22d3ee" },
        ];
      }
      return [
        { start: "#00d4aa", end: "#5ff5d8" },
        { start: "#0a2149", end: "#243b53" },
        { start: "#00d4aa", end: "#00a080" },
        { start: "#627d98", end: "#829ab1" },
        { start: "#0a2149", end: "#334e68" },
        { start: "#00d4aa", end: "#486581" },
        { start: "#627d98", end: "#00d4aa" },
        { start: "#0a2149", end: "#627d98" },
      ];
    };

    // Initialize balls with collision physics
    const initBalls = () => {
      const colors = getColors();
      ballsRef.current = Array.from({ length: 8 }).map((_, i) => {
        const radius = Math.random() * 40 + 30; // 30-70px radius
        const ball: Ball = {
          x: Math.random() * (canvas.width - radius * 2) + radius,
          y: Math.random() * (canvas.height - radius * 2) + radius,
          vx: (Math.random() - 0.5) * 3, // Faster speed: -1.5 to 1.5
          vy: (Math.random() - 0.5) * 3,
          radius,
          color: colors[i % colors.length].start,
          gradient: null,
        };
        
        // Create gradient
        const gradient = ctx.createRadialGradient(
          ball.x, ball.y, 0,
          ball.x, ball.y, ball.radius
        );
        gradient.addColorStop(0, colors[i % colors.length].start);
        gradient.addColorStop(1, colors[i % colors.length].end);
        ball.gradient = gradient;
        
        return ball;
      });
    };
    initBalls();

    // Collision detection and resolution
    const resolveCollisions = () => {
      const balls = ballsRef.current;
      for (let i = 0; i < balls.length; i++) {
        for (let j = i + 1; j < balls.length; j++) {
          const b1 = balls[i];
          const b2 = balls[j];
          
          const dx = b2.x - b1.x;
          const dy = b2.y - b1.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDistance = b1.radius + b2.radius;
          
          if (distance < minDistance) {
            // Collision detected - resolve it
            const angle = Math.atan2(dy, dx);
            const targetX = b1.x + Math.cos(angle) * minDistance;
            const targetY = b1.y + Math.sin(angle) * minDistance;
            
            const ax = (targetX - b2.x) * 0.05; // spring factor
            const ay = (targetY - b2.y) * 0.05;
            
            b1.vx -= ax;
            b1.vy -= ay;
            b2.vx += ax;
            b2.vy += ay;
          }
        }
      }
    };

    // Animation loop
    const animate = () => {
      if (!ctx || !canvas) return;
      
      // Clear canvas with semi-transparent background for trail effect
      ctx.fillStyle = isDarkRef.current ? "#0f172a" : "#f0f4f8";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw balls
      ballsRef.current.forEach((ball) => {
        // Update position
        ball.x += ball.vx;
        ball.y += ball.vy;
        
        // Bounce off walls
        if (ball.x - ball.radius < 0) {
          ball.x = ball.radius;
          ball.vx = Math.abs(ball.vx);
        } else if (ball.x + ball.radius > canvas.width) {
          ball.x = canvas.width - ball.radius;
          ball.vx = -Math.abs(ball.vx);
        }
        
        if (ball.y - ball.radius < 0) {
          ball.y = ball.radius;
          ball.vy = Math.abs(ball.vy);
        } else if (ball.y + ball.radius > canvas.height) {
          ball.y = canvas.height - ball.radius;
          ball.vy = -Math.abs(ball.vy);
        }
        
        // Update gradient position
        if (ball.gradient) {
          ball.gradient = ctx.createRadialGradient(
            ball.x, ball.y, 0,
            ball.x, ball.y, ball.radius
          );
          const colors = getColors();
          const colorIndex = ballsRef.current.indexOf(ball) % colors.length;
          ball.gradient.addColorStop(0, colors[colorIndex].start);
          ball.gradient.addColorStop(1, colors[colorIndex].end);
        }
        
        // Draw ball with soft blurred edges
        ctx.save();
        
        // Create soft edge with radial gradient
        const edgeGradient = ctx.createRadialGradient(
          ball.x, ball.y, ball.radius * 0.7,
          ball.x, ball.y, ball.radius
        );
        const colors = getColors();
        const colorIndex = ballsRef.current.indexOf(ball) % colors.length;
        edgeGradient.addColorStop(0, colors[colorIndex].start + 'CC'); // 80% opacity center
        edgeGradient.addColorStop(0.5, colors[colorIndex].start + '80'); // 50% opacity middle
        edgeGradient.addColorStop(1, colors[colorIndex].end + '00'); // 0% opacity edge
        
        // Draw the soft ball
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
        ctx.fillStyle = edgeGradient;
        ctx.fill();
        
        // Add inner glow/core
        ctx.globalAlpha = 0.6;
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, ball.radius * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = ball.gradient || ball.color;
        ctx.fill();
        
        // Add outer glow effect
        ctx.shadowBlur = 30;
        ctx.shadowColor = colors[colorIndex].start + '60';
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, ball.radius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = colors[colorIndex].start + '40';
        ctx.fill();
        
        ctx.restore();
      });
      
      // Resolve collisions
      resolveCollisions();
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="background-canvas"
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
  );
};

export default Background;
